import { useEffect, useRef, useState } from 'react'
import Styles from './V4PrepareTopics.module.scss'
import { Button } from 'blaze-ui/Button'
import { Heart, MessageCircle, Send1, Favourite } from 'blaze-ui/icons/24'
import { useBrandPlanSchedule } from 'hooks/queries/schedules/useBrandPlanSchedule'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import cx from 'classnames'
import { useLoaderAnimation, scheduleHeadlines } from '../v4LoaderUtils'
import { V4LoaderArrow } from '../V4LoaderArrow'
import { TopBar } from '../TopBar/TopBar'
import { REACT_APP_ENV } from 'env'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const ICON_URL =
  'https://res.cloudinary.com/almanac/image/upload/v1775757475/onboarding_assets/radiant_health_icon_c80rnl.png'

const SETS = [
  {
    thumbnail: 'https://res.cloudinary.com/almanac/image/upload/v1775481604/onboarding_assets/thumbnail_wxes65.png',
    preview: 'https://res.cloudinary.com/almanac/image/upload/v1775482624/onboarding_assets/examplepost_fgxd2a.png',
    topic:
      'Encouraging creative professionals to share their work-in-progress openly — building a brand around creative transparency, not just finished results.',
  },
  {
    thumbnail: 'https://res.cloudinary.com/almanac/image/upload/v1775757477/onboarding_assets/thumbnail2_u2v8sa.png',
    preview: 'https://res.cloudinary.com/almanac/image/upload/v1775757479/onboarding_assets/preview2_kv4xq9.png',
    topic:
      'A new global partnership with tennis pro Sofia Kenin positions FP Movement as a brand that belongs in competitive athletics — not just the studio.',
  },
  {
    thumbnail: 'https://res.cloudinary.com/almanac/image/upload/v1775757477/onboarding_assets/thumbnail3_pxtn4t.png',
    preview: 'https://res.cloudinary.com/almanac/image/upload/v1775757478/onboarding_assets/preview3_xs60gl.png',
    topic:
      'A Spring/Summer 2026 puffer jacket that bridges outdoor performance and everyday style — positioning the brand as outerwear you reach for beyond the trail.',
  },
]

const LONGEST_TOPIC = SETS.reduce((a, b) => (a.topic.length > b.topic.length ? a : b)).topic

const HEADLINE_PHASES = [
  { at: 0, text: 'Generating your topics...' },
  { at: 5000, text: 'Crafting each topic...' },
  { at: 10000, text: 'Finalizing your topics...' },
]

const TW_SPEED = 28
const TW_JITTER = 7
const TOPIC_PAUSE = 1000

const hasTopics = (schedule: ReturnType<typeof useBrandPlanSchedule>['data']): boolean => {
  if (!schedule) return false
  return schedule.contents.length > 0 && schedule.contents.every((c) => !!c.topic)
}

const TERMINAL_STATUSES = new Set(['successful', 'failed'])

const allReferenceImagesTerminal = (schedule: ReturnType<typeof useBrandPlanSchedule>['data']): boolean => {
  if (!schedule || schedule.contents.length === 0) return false
  return schedule.contents.every((c) => TERMINAL_STATUSES.has(c.reference_image?.status ?? ''))
}

interface Props {
  scheduleId: number
  brandKitId?: number
  onReady: () => void
  onBack: () => void
}

export const V4PrepareTopics = ({ scheduleId, brandKitId, onReady, onBack }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const advancedRef = useRef(false)

  const { data: schedule } = useBrandPlanSchedule(scheduleId, {
    placeholderData: undefined,
    refetchOnMount: 'always',
    refetchInterval: (data) => {
      if (!data) return 1000
      if (hasTopics(data) && allReferenceImagesTerminal(data)) return false
      return 1000
    },
  })

  const { data: brandKitData } = useBrandKit(brandKitId, {
    pollUntilBadImageDetection: true,
    staleTime: 0,
  })

  const [currentSet, setCurrentSet] = useState(0)
  const [imagesExiting, setImagesExiting] = useState(false)
  const [topicText, setTopicText] = useState('')
  const [topicExiting, setTopicExiting] = useState(false)
  const [topicWriting, setTopicWriting] = useState(true)

  const { addTimer, clearTimers, typewrite, activeHeadline, setActiveHeadline, exitHeadline, swapHeadline } =
    useLoaderAnimation(TW_SPEED, TW_JITTER)

  const animationStartedRef = useRef(false)

  const runSet = (setIdx: number) => {
    const set = SETS[setIdx % SETS.length]

    setTopicWriting(true)
    typewrite(set.topic, setTopicText, () => {
      setTopicWriting(false)
      addTimer(() => {
        const nextIdx = (setIdx + 1) % SETS.length
        setTopicExiting(true)
        setImagesExiting(true)
        addTimer(() => {
          setTopicText('')
          setTopicExiting(false)
          setCurrentSet(nextIdx)
          setImagesExiting(false)
          runSet(nextIdx)
        }, 350)
      }, TOPIC_PAUSE)
    })
  }

  useEffect(() => {
    if (animationStartedRef.current) return
    animationStartedRef.current = true

    scheduleHeadlines(HEADLINE_PHASES, setActiveHeadline, swapHeadline, addTimer)
    addTimer(() => runSet(0), 400)

    return clearTimers
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!schedule) return
    if (advancedRef.current) return
    if (!hasTopics(schedule)) return
    if (!allReferenceImagesTerminal(schedule)) return
    if (brandKitId && !brandKitData?.brand_kit.bad_image_detection_completed) return

    advancedRef.current = true
    onReady()
  }, [schedule, brandKitData, brandKitId, onReady])

  return (
    <div className={Styles.page}>
      <TopBar highlightStep="campaign" />
      <div className={Styles.wrap}>
        <div className={Styles.copyBlock}>
          <div className={Styles.statusRow}>
            <span>In progress</span>
            <span className={Styles.dot}>.</span>
            <span className={Styles.dot}>.</span>
            <span className={Styles.dot}>.</span>
          </div>

          <div className={Styles.headlineSlot}>
            {exitHeadline && (
              <div className={cx(Styles.headline, Styles.headlineExit)} key={`exit-${exitHeadline}`}>
                {exitHeadline}
              </div>
            )}
            <div className={cx(Styles.headline, Styles.headlineEnter)} key={`active-${activeHeadline}`}>
              {activeHeadline}
            </div>
          </div>

          <p className={Styles.subText}>
            {isMobile
              ? 'Every post starts with a topic and a reference image. Here\u2019s how it comes together:'
              : 'Every post starts with two things: a topic that sets the direction, and a reference image that keeps it looking like you. Here\u2019s how it comes together:'}
          </p>
        </div>

        <hr className={Styles.divider} />

        <div className={Styles.illusRow}>
          <div className={Styles.topicCol}>
            <p className={Styles.illusLabel}>Topic inside your campaign:</p>
            <div className={Styles.topicRow}>
              <div className={Styles.thumbnail}>
                <img
                  src={SETS[currentSet].thumbnail}
                  alt="Reference"
                  className={cx(Styles.thumbnailImg, { [Styles.imagesExiting]: imagesExiting })}
                />
                <div className={Styles.thumbnailOverlay}>
                  <span className={Styles.thumbnailOverlayText}>Reference image</span>
                </div>
              </div>

              <div
                className={cx(Styles.topicTextCard, { [Styles.writing]: topicWriting, [Styles.done]: !topicWriting })}
              >
                <div className={Styles.topicText}>
                  <span className={cx(Styles.topicTextContent, { [Styles.exiting]: topicExiting })}>
                    {topicText}
                    {topicWriting && <span className={cx(Styles.cursor, Styles.cursorOn)} />}
                  </span>
                  <span className={Styles.topicTextReserve} aria-hidden>
                    {LONGEST_TOPIC}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className={Styles.arrowCol}>
            <V4LoaderArrow />
          </div>

          <div className={Styles.cardCol}>
            <p className={Styles.illusLabel}>Generated post:</p>
            <div className={Styles.igCard}>
              <div className={Styles.igHeader}>
                <div className={Styles.igAvatar}>
                  <img src={ICON_URL} alt="" className={Styles.igAvatarImg} />
                </div>
                <div className={Styles.igMeta}>
                  <span className={Styles.igUsername}>radiant_health</span>
                  <span className={Styles.igTime}>Just now</span>
                </div>
              </div>

              <div className={Styles.igImage}>
                <img
                  src={SETS[currentSet].preview}
                  alt="Post preview"
                  className={cx(Styles.igPhoto, { [Styles.imagesExiting]: imagesExiting })}
                />
              </div>

              <div className={Styles.igDots}>
                <span className={cx(Styles.igDot, Styles.igDotActive)} />
                <span className={Styles.igDot} />
                <span className={Styles.igDot} />
                <span className={Styles.igDot} />
                <span className={Styles.igDot} />
              </div>

              <div className={Styles.igFooter}>
                <div className={Styles.igActions}>
                  <div className={Styles.igIcons}>
                    <Heart size={16} color="var(--dark-40)" />
                    <MessageCircle size={16} color="var(--dark-40)" />
                    <Send1 size={16} color="var(--dark-40)" />
                  </div>
                  <Favourite size={16} color="var(--dark-40)" />
                </div>
                <p className={Styles.igCaption}>
                  <strong>radiant_health</strong> Discover the joyful playtime moments...
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className={Styles.footer}>
        <div className={Styles.cancelBtn}>
          <Button variant="tertiary" size="lg" onPress={onBack}>
            Back
          </Button>
        </div>
        {REACT_APP_ENV === 'dev' && (
          <div style={{ pointerEvents: 'all' }}>
            <Button
              variant="secondary"
              size="lg"
              onPress={() => {
                advancedRef.current = true
                onReady()
              }}
            >
              Skip (dev)
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
